/**
 * The wall-clock discipline behind the passkey sign-in hook.
 *
 * Everything the hook awaits belongs to somebody else — a browser ceremony, an
 * SDK round trip — and none of it is guaranteed to settle. A promise that never
 * settles runs no `finally`, so an unbounded `await` anywhere in the flow leaves
 * `isLoading` true forever, and the login page folds that into
 * `loginControlsDisabled`: one press disables Google, Microsoft, email AND
 * password until the app is restarted. A latch, not a dead button.
 *
 * So every await in that flow is bounded, by one of the two windows below.
 * "`isLoading` always settles" is a claim about ALL of them, not just the
 * ceremony — bounding only the slowest one moves the latch rather than closing
 * it.
 */

/**
 * Wall-clock cap on the browser ceremony.
 *
 * A WebAuthn promise is not guaranteed to settle at all. Measured in the Electron
 * shell (`docs/solutions/ui-bugs/desktop-entry-canvas-token-pinning-and-webauthn.md`):
 * the ceremony is still pending at 30s with no OS sheet, no rejection, and the
 * permission handler never invoked — it ignores its own `timeout` option and its
 * abort signal alike.
 *
 * Hence a RACE rather than a signal. The signal is still aborted when the cap
 * wins — it is what makes a browser that honours it close its sheet — but nothing
 * downstream may be trusted to settle the promise, so nothing downstream is what
 * releases the UI.
 *
 * The value is sized as a LATCH-BREAKER, not as a UX timeout: it has to sit past
 * the slowest ceremony a real user can legitimately run, because every firing
 * that is not a wedged browser cancels somebody's sign-in. The shell that
 * motivated the cap does not reach it at all — the button is hidden there by the
 * capability pin — which leaves it firing on the web, where cross-device QR and
 * security-key-plus-PIN flows routinely run into minutes. A cap near the 30s
 * measurement window would therefore cancel far more real sign-ins than hung
 * ones. 180s stays under WebAuthn's own 300s default envelope, so a browser that
 * honours `timeout` still rejects first and keeps its own error taxonomy.
 */
export const CEREMONY_TIMEOUT_MS = 180_000;

/**
 * Cap on the SDK calls that follow a ceremony — the AAL lookup, the TOTP
 * verify, reading and revoking the stored session.
 *
 * Much tighter than the ceremony cap because it bounds a different thing: an
 * HTTP round trip, not a human-paced gesture at an OS sheet. These calls can
 * still hang indefinitely — `getSession` and `signOut` take the SDK's
 * cross-tab session lock, which a wedged sibling tab can hold for as long as it
 * likes — and each of them sits on a path that would otherwise latch the page.
 */
export const AUTH_REQUEST_TIMEOUT_MS = 30_000;

/**
 * What a race resolves with when the ceremony cap wins. A symbol rather than a
 * new `PasskeyErrorCode`: the adapter's taxonomy is a closed set of MEASURED
 * server and browser outcomes, and a client-side deadline is neither.
 */
export const CEREMONY_TIMED_OUT = Symbol('passkeyCeremonyTimedOut');

/**
 * One attempt's cap, held by that attempt and cancellable only through this
 * handle.
 *
 * Per-attempt rather than one shared timer ref, because the cap FREES the
 * single-flight latch on purpose: a stale attempt can still be running when a
 * newer one installs its own cap, and a shared handle lets the stale one's
 * `finally` cancel the newer one's. The successor would then be running with no
 * cap at all — the latch back, one attempt later.
 */
export interface CeremonyDeadline {
  /** Resolves to `CEREMONY_TIMED_OUT` if the window closes before `work` settles. */
  readonly race: <T>(work: Promise<T>) => Promise<T | typeof CEREMONY_TIMED_OUT>;
  /** Idempotent, so every exit can drop the timer without coordinating. */
  readonly cancel: () => void;
}

export function startCeremonyDeadline(): CeremonyDeadline {
  let timer: ReturnType<typeof setTimeout> | null = null;
  const expiry = new Promise<typeof CEREMONY_TIMED_OUT>((resolve) => {
    timer = setTimeout(() => {
      timer = null;
      resolve(CEREMONY_TIMED_OUT);
    }, CEREMONY_TIMEOUT_MS);
  });

  return {
    race: async <T>(work: Promise<T>): Promise<T | typeof CEREMONY_TIMED_OUT> => {
      markHandled(work);
      return await Promise.race([work, expiry]);
    },
    cancel: (): void => {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    },
  };
}

/**
 * Bounds one SDK call, standing `fallback` in if the window closes first.
 *
 * A value rather than a rejection because every caller already has a defined
 * answer for "the request could not tell me": an AAL lookup that cannot answer
 * is `indeterminate` and fails closed, a verify that cannot answer is a failed
 * verify. Rejecting instead would route a timeout into the ceremony's error
 * taxonomy, which describes browser and server outcomes and has no member for
 * a client-side deadline.
 *
 * The timer is dropped on every exit, so a settled call leaves nothing pending.
 */
export async function withRequestDeadline<T>(work: Promise<T>, fallback: T): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  markHandled(work);
  try {
    return await Promise.race([
      work,
      new Promise<T>((resolve) => {
        timer = setTimeout(() => {
          resolve(fallback);
        }, AUTH_REQUEST_TIMEOUT_MS);
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Attaches a no-op handler so a rejection arriving AFTER the deadline won is not
 * reported as unhandled. The race still sees a rejection that arrives first —
 * this adds a handler, it does not consume the outcome.
 */
function markHandled(work: Promise<unknown>): void {
  void work.catch(() => {
    // Nothing to do: the race above is the only consumer of the outcome. This
    // handler exists so the rejection counts as observed.
  });
}
